import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Clock, Lock, Radio } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { fetchLiveGamesDashboard, LiveGameSummary } from '../api/liveGames';
import { RockColor } from '../types/curling';

const ROCK_COLOR_HEX: Record<RockColor, string> = {
  yellow: '#fbbf24',
  green: '#10b981',
  blue: '#3b82f6',
  red: '#ef4444',
  white: '#f3f4f6',
};

/** How often the dashboard re-polls while open, so scores update without a refresh. */
const REFRESH_MS = 20_000;

function RockSwatch({ color }: { color?: RockColor }) {
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ background: color ? ROCK_COLOR_HEX[color] : 'rgba(255,255,255,0.25)' }}
    />
  );
}

function relativeTime(epochMs: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - epochMs) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function GameCard({
  game, onClick, live, clickable,
}: { game: LiveGameSummary; onClick: () => void; live: boolean; clickable: boolean }) {
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      title={clickable ? undefined : 'Register and sign in to open this game'}
      className={`w-full text-left backdrop-blur-sm border border-white/15 rounded-xl p-4 transition-colors ${
        clickable ? 'bg-white/8 hover:bg-white/12 cursor-pointer' : 'bg-white/8 opacity-70 cursor-not-allowed'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="text-xs text-blue-300/70 truncate">
          {[game.curlingClub, game.sheetNumber && `Sheet ${game.sheetNumber}`].filter(Boolean).join(' • ') || 'Location not set'}
        </div>
        {live ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 flex-shrink-0">
            <Radio className="w-3 h-3 animate-pulse" />
            LIVE
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-blue-300/50 flex-shrink-0">
            <Clock className="w-3 h-3" />
            {relativeTime(game.lastActivityAt)}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <RockSwatch color={game.team1RockColor} />
          <span className="text-white font-medium truncate">{game.team1Name}</span>
        </div>
        <span className="text-xl font-bold text-white flex-shrink-0">{game.team1Score}</span>
      </div>
      <div className="flex items-center justify-between gap-4 mt-1">
        <div className="flex items-center gap-2 min-w-0">
          <RockSwatch color={game.team2RockColor} />
          <span className="text-white font-medium truncate">{game.team2Name}</span>
        </div>
        <span className="text-xl font-bold text-white flex-shrink-0">{game.team2Score}</span>
      </div>

      <p className="text-xs text-blue-300/50 mt-2">
        {game.endsPlayed} {game.endsPlayed === 1 ? 'end' : 'ends'} recorded
      </p>
    </button>
  );
}

export function LiveGames() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [live, setLive] = useState<LiveGameSummary[] | null>(null);
  const [recentlyCompleted, setRecentlyCompleted] = useState<LiveGameSummary[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      fetchLiveGamesDashboard()
        .then(dashboard => {
          if (cancelled) return;
          setLive(dashboard.live);
          setRecentlyCompleted(dashboard.recentlyCompleted);
          setError('');
        })
        .catch(() => {
          if (!cancelled) setError('Could not load live games. Retrying…');
        });
    };

    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Live Games</h1>
            <p className="text-blue-300">Watch every game currently being recorded, anywhere on Black Rock Curling.</p>
          </div>
          <button
            onClick={() => navigate(user ? '/options' : '/')}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
            {user ? 'Back' : 'Sign In'}
          </button>
        </div>

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        {!user && (
          <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-400/30 rounded-xl px-4 py-3 mb-6">
            <Lock className="w-4 h-4 text-amber-300 flex-shrink-0" />
            <p className="text-sm text-amber-200">
              Please register and choose Viewer to see detailed game views.
            </p>
          </div>
        )}

        <section className="mb-10">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Radio className="w-5 h-5 text-red-400" />
            Live Now
          </h2>
          {live === null ? (
            <p className="text-blue-300">Loading…</p>
          ) : live.length === 0 ? (
            <p className="text-sm text-blue-300/70">No games are being recorded right now.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {live.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  live
                  clickable={!!user}
                  onClick={() => navigate(`/live-games/${game.id}`)}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-1">Completed Recently</h2>
          <p className="text-xs text-slate-400 mb-4">Games finished in the last 3 days.</p>
          {recentlyCompleted === null ? (
            <p className="text-blue-300">Loading…</p>
          ) : recentlyCompleted.length === 0 ? (
            <p className="text-sm text-blue-300/70">No games have finished in the last 3 days.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {recentlyCompleted.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  live={false}
                  clickable={!!user}
                  onClick={() => navigate(`/live-games/${game.id}`)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
